class HeroBanner extends HTMLElement {
  constructor() {
    super();

    this.EFFECTS = new Set(["fade", "slide", "zoom", "reveal"]);
    this.SPEEDS = new Set(["fast", "normal", "slow"]);
    this.SPEED_MAP = {
      fast: { fade: 220, slide: 260, zoom: 300, reveal: 240 },
      normal: { fade: 320, slide: 360, zoom: 420, reveal: 320 },
      slow: { fade: 420, slide: 480, zoom: 560, reveal: 420 },
    };

    this.onNextClick = this.goNext.bind(this);
    this.onPrevClick = this.goPrev.bind(this);
    this.onTouchStart = this.handleTouchStart.bind(this);
    this.onTouchMove = this.handleTouchMove.bind(this);
    this.onTouchEnd = this.handleTouchEnd.bind(this);

    this.startX = 0;
    this.startY = 0;
    this.deltaX = 0;
    this.isSwiping = false;
    this.lastSwipeAt = 0;
    this.SWIPE_THRESHOLD = 40;
    this.SWIPE_COOLDOWN = 250;
  }

  connectedCallback() {
    if (this.dataset.sliderInit === "true") return;
    this.dataset.sliderInit = "true";

    this.slideWrapper = this.querySelector(".hero-banner__slide-wrapper");
    this.slideControls = this.querySelector(".hero-banner__slide-controls");
    this.prevButton = this.querySelector(".hero-banner__slide-controls-prev");
    this.nextButton = this.querySelector(".hero-banner__slide-controls-next");
    this.dotsWrap = this.querySelector(".hero-banner__dots");
    this.dots = Array.from(this.querySelectorAll(".hero-banner__dot"));

    if (
      !this.slideWrapper ||
      !this.slideControls ||
      !this.prevButton ||
      !this.nextButton
    )
      return;

    this.slides = Array.from(this.slideWrapper.children);
    this.slides.forEach((slide, index) => {
      slide.dataset.slideIndex = String(index);
    });

    if (this.slides.length < 2) {
      this.slideControls.classList.remove("show-controls");
      if (this.dotsWrap) this.dotsWrap.style.display = "none";
      return;
    }

    this.slideControls.classList.add("show-controls");
    this.applySpeedVars();

    this.nextButton.addEventListener("click", this.onNextClick);
    this.prevButton.addEventListener("click", this.onPrevClick);

    this.dotHandlers = [];
    this.dots.forEach((dot) => {
      const handler = () => {
        const targetIndex = Number(dot.dataset.dotIndex);
        this.goToIndex(targetIndex);
      };
      this.dotHandlers.push({ dot, handler });
      dot.addEventListener("click", handler);
    });

    this.slideWrapper.addEventListener("touchstart", this.onTouchStart, {
      passive: true,
    });
    this.slideWrapper.addEventListener("touchmove", this.onTouchMove, {
      passive: true,
    });
    this.slideWrapper.addEventListener("touchend", this.onTouchEnd, {
      passive: true,
    });

    this.updateDots();
  }

  disconnectedCallback() {
    this.nextButton?.removeEventListener("click", this.onNextClick);
    this.prevButton?.removeEventListener("click", this.onPrevClick);

    this.dotHandlers?.forEach(({ dot, handler }) =>
      dot.removeEventListener("click", handler),
    );

    this.slideWrapper?.removeEventListener("touchstart", this.onTouchStart);
    this.slideWrapper?.removeEventListener("touchmove", this.onTouchMove);
    this.slideWrapper?.removeEventListener("touchend", this.onTouchEnd);
  }

  getEffect() {
    const v = this.dataset.animationEffect;
    return this.EFFECTS.has(v) ? v : "fade";
  }

  getSpeed() {
    const v = this.dataset.animationSpeed;
    return this.SPEEDS.has(v) ? v : "normal";
  }

  applySpeedVars() {
    const cfg = this.SPEED_MAP[this.getSpeed()];
    this.style.setProperty("--hero-fade-ms", `${cfg.fade}ms`);
    this.style.setProperty("--hero-slide-ms", `${cfg.slide}ms`);
    this.style.setProperty("--hero-zoom-ms", `${cfg.zoom}ms`);
    this.style.setProperty("--hero-reveal-ms", `${cfg.reveal}ms`);
  }

  resetAnimationClasses(el) {
    el.classList.remove(
      "anim-fade",
      "anim-slide-next",
      "anim-slide-prev",
      "anim-zoom",
      "anim-reveal",
    );
  }

  animateCurrentSlide(direction) {
    const currentSlide = this.slideWrapper.firstElementChild;
    if (!currentSlide) return;

    this.applySpeedVars();
    const effect = this.getEffect();

    this.resetAnimationClasses(currentSlide);
    currentSlide.offsetHeight;

    if (effect === "fade") currentSlide.classList.add("anim-fade");
    else if (effect === "slide")
      currentSlide.classList.add(
        direction === "prev" ? "anim-slide-prev" : "anim-slide-next",
      );
    else if (effect === "zoom") currentSlide.classList.add("anim-zoom");
    else if (effect === "reveal") currentSlide.classList.add("anim-reveal");

    currentSlide.addEventListener(
      "animationend",
      () => this.resetAnimationClasses(currentSlide),
      { once: true },
    );
  }

  updateDots() {
    if (!this.dots.length) return;

    const currentSlide = this.slideWrapper.firstElementChild;
    const activeIndex = Number(currentSlide?.dataset.slideIndex || 0);

    this.dots.forEach((dot, index) => {
      const isActive = index === activeIndex;
      dot.classList.toggle("is-active", isActive);
      dot.setAttribute("aria-current", isActive ? "true" : "false");
    });
  }

  goNext() {
    this.slideWrapper.appendChild(this.slideWrapper.firstElementChild);
    this.animateCurrentSlide("next");
    this.updateDots();
  }

  goPrev() {
    this.slideWrapper.prepend(this.slideWrapper.lastElementChild);
    this.animateCurrentSlide("prev");
    this.updateDots();
  }

  goToIndex(targetIndex) {
    let guard = 0;
    while (guard < this.slides.length) {
      const currentIndex = Number(
        this.slideWrapper.firstElementChild?.dataset.slideIndex || 0,
      );
      if (currentIndex === targetIndex) break;
      this.slideWrapper.appendChild(this.slideWrapper.firstElementChild);
      guard += 1;
    }
    this.animateCurrentSlide("next");
    this.updateDots();
  }

  handleTouchStart(e) {
    const touch = e.changedTouches[0];
    this.startX = touch.clientX;
    this.startY = touch.clientY;
    this.deltaX = 0;
    this.isSwiping = false;
  }

  handleTouchMove(e) {
    const touch = e.changedTouches[0];
    const dx = touch.clientX - this.startX;
    const dy = touch.clientY - this.startY;

    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 8) {
      this.isSwiping = true;
      this.deltaX = dx;
    }
  }

  handleTouchEnd() {
    if (!this.isSwiping) return;

    const now = Date.now();
    if (now - this.lastSwipeAt < this.SWIPE_COOLDOWN) return;

    if (Math.abs(this.deltaX) >= this.SWIPE_THRESHOLD) {
      if (this.deltaX < 0) this.goNext();
      else this.goPrev();
      this.lastSwipeAt = now;
    }
  }
}

if (!customElements.get("hero-banner")) {
  customElements.define("hero-banner", HeroBanner);
}
